import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { actions } from "../../../pages/Applications/applications.reducers";
import { selectShouldShowLoadingPage } from "../../../pages/DataModels";
import {
  selectShouldShowSidePanel,
  selectShouldShowMenuBar,
} from "../../../pages/Applications";
import { leftDashArrowIcon } from "../../../assets/icons";
import { TABS, INDEX, MOUSE_EVENTS, EMPTY_STRING } from "../../../constants";

const { setShouldShowSidePanel, setShouldShowMenuBar } = actions;
const { APPLICATIONS } = TABS;
const { MOUSE_DOWN } = MOUSE_EVENTS;

const { SECOND, THIRD, FOURTH, FIFTH } = INDEX;
const SERVICES = "services";

const Menubar = ({ options }) => {
  const ref = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showMenuBar = useSelector(selectShouldShowMenuBar);
  const shouldShowSidePanel = useSelector(selectShouldShowSidePanel);
  const isLoadingPage = useSelector(selectShouldShowLoadingPage);
  const [id, setId] = useState(EMPTY_STRING);
  const [serviceId, setServiceId] = useState(EMPTY_STRING);
  const [selectedMenu, setSelectedMenu] = useState(APPLICATIONS);
  const [selectedSubMenu, setSelectedSubMenu] = useState();
  const [clickedOutsideSidePanel, setClickedOutsideSidePanel] = useState(false);
  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (shouldShowSidePanel && ref.current && !ref.current.contains(e.target))
        dispatch(setShouldShowSidePanel(false));
    };
    document.addEventListener(MOUSE_DOWN, checkIfClickedOutside);
    return () =>
      document.removeEventListener(MOUSE_DOWN, checkIfClickedOutside);
  }, [clickedOutsideSidePanel, dispatch, shouldShowSidePanel]);

  useEffect(() => {
    const urlArray = window.location.pathname.toString().split("/");

    setId(urlArray[THIRD]);
    setServiceId(urlArray[FIFTH]);
    const getActiveTab = (selectedTab) => selectedTab;

    let selectedTab = APPLICATIONS;
    if (urlArray.length >= SECOND && urlArray[SECOND] === "user-roles")
      selectedTab = "users";
    else if (urlArray.length >= SECOND) selectedTab = urlArray[SECOND];

    const selectedSubTab =
      urlArray.length >= FOURTH && urlArray[FOURTH];

    setSelectedMenu(getActiveTab(selectedTab));
    setSelectedSubMenu(getActiveTab(selectedSubTab));
    !selectedSubTab && dispatch(setShouldShowMenuBar(false))
  }, [navigate, dispatch, serviceId, id, isLoadingPage]);

  useEffect(() => {
    let selectedTab = APPLICATIONS;

    const urlArray = window.location.pathname.toString().split("/");
    const activeTab = (selectedTab) => selectedTab;
    if (urlArray.length >= 4) {
      selectedTab = urlArray[3];
      setSelectedSubMenu(activeTab(selectedTab));
    } else if (urlArray[1] === "user-roles") {
      selectedTab = "users";
      setSelectedMenu(activeTab(selectedTab));
    } else if (urlArray.length > 1) {
      selectedTab = urlArray[1];
      setSelectedMenu(activeTab(selectedTab));
    }
  }, [navigate]);

  useEffect(() => {
    if (!isLoadingPage && id) dispatch(setShouldShowMenuBar(true));
  }, [dispatch, id, isLoadingPage]);

  return (
    <>
      <div
        className={classNames(
          "sidebar bg-gray-900 h-screen top-0 z-50 fixed lg:static lg:h-auto w-72 border-r border-gray-800 text-sm transition-all duration-300",
          {
            "left-0": shouldShowSidePanel === true,
            "-left-72": shouldShowSidePanel === false,
            "lg:w-auto": showMenuBar || isLoadingPage,
          }
        )}
        onClick={() => setClickedOutsideSidePanel((prevState) => !prevState)}
        ref={ref}
      >
        <div className="border-b border-gray-800 block lg:hidden">
          <button
            className="px-4 py-5 flex items-center"
            onClick={() => dispatch(setShouldShowSidePanel(false))}
          >
            <img
              src={leftDashArrowIcon}
              className="mr-3 w-5"
              alt="left-dash-arrow-icon"
            />
            <span className="text-sm text-gray-400">Close</span>
          </button>
        </div>
        <ul className="p-4 border-b border-gray-800">
          {options.map((option) => (
            <li
              className="mb-1"
              key={option.id}
              onClick={() => dispatch(setShouldShowSidePanel(false))}
            >
              <button
                className={classNames(
                  "flex items-center rounded-lg py-3 px-3 text-blue-light hover:bg-gray-800 hover:text-white cursor-pointer w-full disabled:cursor-not-allowed",
                  {
                    "bg-gray-800 text-white ": selectedMenu === option.path,
                    "text-gray-300": selectedMenu !== option.path,
                  }
                )}
                disabled={isLoadingPage}
                onClick={() => {
                  setSelectedMenu(option.path);
                  navigate(`/${option.path}`);
                }}
              >
                <div>
                  <img
                    src={option.image}
                    className={classNames("icon w-5 h-5", {
                      "!brightness-0 !invert": selectedMenu === option.path,
                    })}
                    alt={option.name}
                  />
                </div>
                <span
                  className={classNames("ml-4", {
                    "lg:hidden": showMenuBar || isLoadingPage,
                  })}
                >
                  {option.name}
                </span>
                {option.count && (
                  <span
                    className={classNames(
                      "bg-gray-700 text-xs leading-4 font-medium px-3 rounded-xl py-[2px] ml-auto",
                      {
                        "lg:hidden": showMenuBar || isLoadingPage,
                      }
                    )}
                  >
                    {option.count}
                  </span>
                )}
              </button>
              {showMenuBar && option.path === selectedMenu && (
                <ul className="sub-menu ml-5 pl-3 pt-3 mb-4 text-xs block lg:hidden border-l border-gray-700">
                  {option.child.map((subMenu) => (
                    <li className="mb-1 relative" key={subMenu.id}>
                      <div className="absolute w-3 border-t border-gray-700 right-full top-1/2 translate-y-1/2"></div>
                      <div
                        className={classNames(
                          "flex items-center rounded-md px-2 py-2 cursor-pointer hover:bg-gray-800 hover:text-white",
                          {
                            "bg-gray-800 text-white":
                              subMenu.path === selectedSubMenu,
                            "text-gray-300 ": subMenu.path !== selectedSubMenu,
                          }
                        )}
                        onClick={() => {
                          setSelectedSubMenu(subMenu.path);
                          navigate(`/${option.path}/${id}/${subMenu.path}`);
                        }}
                      >
                        <div>
                          <img
                            src={subMenu.image}
                            className={classNames("icon w-5 h-5", {
                              "!brightness-0 !invert":
                                subMenu.path === selectedSubMenu,
                            })}
                            alt={subMenu.name}
                          />
                        </div>
                        <span className="ml-2">{subMenu.name}</span>
                        {subMenu.count && (
                          <span className="bg-gray-700 text-xs leading-4 font-medium px-2 rounded-xl ml-auto ">
                            {subMenu.count}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
      {options.map(
        (option) =>
          showMenuBar &&
          option.path === selectedMenu && (
            <div
              className={classNames(
                "sidebar hidden bg-gray-900 h-auto w-72 border-r border-gray-800 text-sm",
                {
                  "lg:block": !(serviceId && selectedSubMenu === SERVICES),
                }
              )}
              key={option.id}
            >
              <ul className="p-4 border-b border-gray-800">
                {option.child.map((subMenu) => (
                  <li className="mb-1" key={subMenu.id}>
                    <div
                      className={classNames(
                        "flex items-center rounded-lg py-3 px-3 text-blue-light cursor-pointer hover:bg-gray-800 hover:text-white",
                        {
                          "bg-gray-800 text-white":
                            subMenu.path === selectedSubMenu,
                          "text-gray-300 ": subMenu.path !== selectedSubMenu,
                        }
                      )}
                      onClick={() => {
                        setSelectedSubMenu(subMenu.path);
                        navigate(`/${selectedMenu}/${id}/${subMenu.path}`);
                      }}
                    >
                      <div>
                        <img
                          src={subMenu.image}
                          className={classNames("icon w-5 h-5", {
                            "!brightness-0 !invert":
                              subMenu.path === selectedSubMenu,
                          })}
                          alt={subMenu.name}
                        />
                      </div>
                      <span className="ml-4">{subMenu.name}</span>
                      {subMenu.count && (
                        <span className="bg-gray-700 text-xs leading-4 font-medium px-3 rounded-xl py-[2px] ml-auto ">
                          {subMenu.count}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )
      )}
    </>
  );
};

export default Menubar;
